import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentAssignmentsApi, departmentRolesApi, departmentsApi } from '../api/departmentsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type {
  CreateDepartmentPayload,
  CreateDepartmentRolePayload,
  CreateStaffAssignmentPayload,
  RolePermissionEntry,
  UpdateDepartmentPayload,
  UpdateDepartmentRolePayload,
} from '../types';

const KEY = 'departments';

/** Nest validation errors arrive as `message: string[]`; WatchmanFetch already takes the first. */
const errMsg = (err: unknown, fallback: string) =>
  (typeof (err as { message?: unknown })?.message === 'string' && (err as { message: string }).message) || fallback;

export const useDepartments = () =>
  useQuery({ queryKey: [KEY, 'list'], queryFn: departmentsApi.list, staleTime: 60 * 1000 });

export const useDepartmentRoles = (departmentId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'roles', departmentId],
    queryFn: () => departmentRolesApi.list(departmentId),
    enabled: !!departmentId,
    staleTime: 60 * 1000,
  });

/** Enabled only while the matrix modal is open — one request per role otherwise. */
export const useDepartmentRolePermissions = (roleId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'role-permissions', roleId],
    queryFn: () => departmentRolesApi.listPermissions(roleId as string),
    enabled: !!roleId,
    staleTime: 30 * 1000,
  });

export const useDepartmentAssignments = (departmentId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'assignments', departmentId],
    queryFn: () => departmentAssignmentsApi.list({ departmentId }),
    enabled: !!departmentId,
    staleTime: 30 * 1000,
  });

const useDeptMutation = <TVars, TData>(
  fn: (vars: TVars) => Promise<TData>,
  success: string,
  fallback: string,
  invalidate: (vars: TVars) => unknown[][],
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, vars) => {
      invalidate(vars).forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      toast.success(success);
    },
    onError: (err: unknown) => toast.error(errMsg(err, fallback)),
  });
};

export const useCreateDepartment = () =>
  useDeptMutation(
    (payload: CreateDepartmentPayload) => departmentsApi.create(payload),
    'Department created',
    'Failed to create department',
    () => [[KEY, 'list']],
  );

export const useUpdateDepartment = () =>
  useDeptMutation(
    ({ id, payload }: { id: string; payload: UpdateDepartmentPayload }) => departmentsApi.update(id, payload),
    'Department updated',
    'Failed to update department',
    () => [[KEY, 'list']],
  );

export const useToggleDepartmentActive = () =>
  useDeptMutation(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive ? departmentsApi.deactivate(id) : departmentsApi.activate(id),
    'Department updated',
    'Failed to update department',
    () => [[KEY, 'list']],
  );

export const useCreateDepartmentRole = () =>
  useDeptMutation(
    (payload: CreateDepartmentRolePayload) => departmentRolesApi.create(payload),
    'Role created',
    'Failed to create role',
    (vars) => [[KEY, 'roles', vars.departmentId]],
  );

export const useUpdateDepartmentRole = () =>
  useDeptMutation(
    ({ id, payload }: { id: string; departmentId: string; payload: UpdateDepartmentRolePayload }) =>
      departmentRolesApi.update(id, payload),
    'Role updated',
    'Failed to update role',
    (vars) => [[KEY, 'roles', vars.departmentId]],
  );

export const useToggleDepartmentRoleActive = () =>
  useDeptMutation(
    ({ id, isActive }: { id: string; departmentId: string; isActive: boolean }) =>
      isActive ? departmentRolesApi.deactivate(id) : departmentRolesApi.activate(id),
    'Role updated',
    'Failed to update role',
    (vars) => [[KEY, 'roles', vars.departmentId]],
  );

export const useSetDepartmentRolePermissions = () =>
  useDeptMutation(
    ({ roleId, permissions }: { roleId: string; permissions: RolePermissionEntry[] }) =>
      departmentRolesApi.setPermissions(roleId, permissions),
    'Permissions saved',
    'Failed to save permissions',
    (vars) => [[KEY, 'role-permissions', vars.roleId]],
  );

export const useCreateStaffAssignment = () =>
  useDeptMutation(
    (payload: CreateStaffAssignmentPayload) => departmentAssignmentsApi.create(payload),
    'Staff member assigned',
    'Failed to assign staff member',
    (vars) => [[KEY, 'assignments', vars.departmentId]],
  );

export const useRemoveStaffAssignment = () =>
  useDeptMutation(
    ({ id }: { id: string; departmentId: string }) => departmentAssignmentsApi.remove(id),
    'Assignment removed',
    'Failed to remove assignment',
    (vars) => [[KEY, 'assignments', vars.departmentId]],
  );
